import { useEffect, useState } from "react";
import { Plus, Copy, ExternalLink, CreditCard, Loader2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, EmptyState } from "@/components/dash";
import StatusBadge from "@/components/StatusBadge";
import CoinIcon from "@/components/CoinIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { API, apiErr } from "@/lib/api";
import { formatTRY, formatDate, truncate } from "@/lib/format";

const FRONT = process.env.REACT_APP_BACKEND_URL;

export default function Payments() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ amount_try: "", customer_email: "", order_id: "", description: "", expires_minutes: 30, success_url: "", webhook_url: "" });

  const load = () => api.get("/payments").then(({ data }) => setItems(data.payments)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/payments", { ...form, amount_try: parseFloat(form.amount_try), expires_minutes: parseInt(form.expires_minutes) || 30 });
      setCreated(data);
      toast.success("Ödeme oluşturuldu.");
      load();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSaving(false);
    }
  };

  const payLink = (id) => `${FRONT}/pay/${id}`;
  const copy = (id) => { navigator.clipboard.writeText(payLink(id)); toast.success("Ödeme linki kopyalandı."); };

  return (
    <div>
      <PageHeader
        title="Ödemeler"
        desc="Kripto ödemelerinizi oluşturun ve yönetin."
        action={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreated(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-payment-btn" className="rounded-full"><Plus size={16} /> Ödeme Oluştur</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>{created ? "Ödeme Hazır" : "Yeni Ödeme"}</DialogTitle></DialogHeader>
              {created ? (
                <div className="space-y-4">
                  <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Payment ID</span><span className="font-mono">{truncate(created.payment_id, 10)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Son Kullanma</span><span>{formatDate(created.expires_at)}</span></div>
                  </div>
                  <div className="mx-auto h-40 w-40 rounded-xl border border-border bg-white p-2">
                    <img src={`${API}/qr?data=${encodeURIComponent(payLink(created.payment_id))}`} alt="QR" className="h-full w-full" />
                  </div>
                  <button onClick={() => copy(created.payment_id)} className="w-full flex items-center gap-2 rounded-xl border border-border bg-muted px-3 py-2.5">
                    <span className="flex-1 text-left text-xs font-mono truncate">{payLink(created.payment_id)}</span>
                    <Copy size={15} className="text-primary" />
                  </button>
                  <a href={payLink(created.payment_id)} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="w-full rounded-full"><ExternalLink size={15} /> Ödeme Sayfasını Aç</Button>
                  </a>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3.5" data-testid="create-payment-form">
                  <div><Label className="text-sm">Tutar (₺)</Label><Input required type="number" step="0.01" min="1" value={form.amount_try} onChange={set("amount_try")} data-testid="pay-amount" className="mt-1.5" placeholder="1000.00" /></div>
                  <div><Label className="text-sm">Müşteri E-posta</Label><Input type="email" value={form.customer_email} onChange={set("customer_email")} data-testid="pay-email" className="mt-1.5" placeholder="musteri@ornek.com" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-sm">Sipariş No</Label><Input value={form.order_id} onChange={set("order_id")} data-testid="pay-order" className="mt-1.5" placeholder="SIP-1042" /></div>
                    <div><Label className="text-sm">Süre (dk)</Label><Input type="number" value={form.expires_minutes} onChange={set("expires_minutes")} data-testid="pay-expiry" className="mt-1.5" /></div>
                  </div>
                  <div><Label className="text-sm">Açıklama</Label><Textarea value={form.description} onChange={set("description")} data-testid="pay-desc" className="mt-1.5 min-h-16" placeholder="Sipariş açıklaması" /></div>
                  <div><Label className="text-sm">Başarılı Ödeme URL</Label><Input value={form.success_url} onChange={set("success_url")} className="mt-1.5" placeholder="https://..." /></div>
                  <div><Label className="text-sm">Webhook URL</Label><Input value={form.webhook_url} onChange={set("webhook_url")} className="mt-1.5" placeholder="https://..." /></div>
                  <Button type="submit" disabled={saving} data-testid="pay-submit" className="w-full rounded-full h-11">{saving ? <Loader2 className="animate-spin" size={16} /> : "Oluştur"}</Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        }
      />

      {items === null ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={CreditCard} title="Henüz ödeme yok" desc="İlk kripto ödemenizi oluşturarak başlayın." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/40">
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Sipariş</th>
                  <th className="px-4 py-3 font-medium">Tutar</th>
                  <th className="px-4 py-3 font-medium">Kripto</th>
                  <th className="px-4 py-3 font-medium">Durum</th>
                  <th className="px-4 py-3 font-medium">Tarih</th>
                  <th className="px-4 py-3 font-medium text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border" data-testid="payments-table">
                {items.map((p) => (
                  <tr key={p.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <p className="font-medium">{p.order_id}</p>
                      <p className="text-xs text-muted-foreground">{p.customer_email || "-"}</p>
                    </td>
                    <td className="px-4 py-3 font-semibold">{formatTRY(p.amount_try)}</td>
                    <td className="px-4 py-3">
                      {p.coin ? <span className="inline-flex items-center gap-1.5"><CoinIcon symbol={p.coin} size={18} /> {p.crypto_amount ? Number(p.crypto_amount).toFixed(4) : "-"}</span> : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{formatDate(p.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => copy(p.id)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-accent" title="Kopyala"><Copy size={15} /></button>
                        <a href={payLink(p.id)} target="_blank" rel="noreferrer" className="h-8 w-8 grid place-items-center rounded-lg hover:bg-accent" title="Aç"><ExternalLink size={15} /></a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
